// Utilitaires de compression d'images côté client (Canvas API, sans dépendance)
// + validation de taille pour images et vidéos.
//
// Pour préserver la qualité premium tout en réduisant le poids, on utilise
// WebP (qualité 85 ≈ JPEG qualité 95 mais ~35% plus léger). Fallback JPEG
// si le navigateur ne supporte pas WebP via canvas (rare).

const MAX_IMAGE_RAW_MB = 25;
const MAX_VIDEO_MB = 50;
const TARGET_MAX_WIDTH = 1800;
const TARGET_QUALITY = 0.85;

let _webpSupportCache = null;
function supportsWebpEncode() {
  if (_webpSupportCache !== null) return _webpSupportCache;
  try {
    const c = document.createElement("canvas");
    c.width = 1; c.height = 1;
    const data = c.toDataURL("image/webp");
    _webpSupportCache = data.startsWith("data:image/webp");
  } catch {
    _webpSupportCache = false;
  }
  return _webpSupportCache;
}

const readAsDataURL = (file) =>
  new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = reject;
    fr.readAsDataURL(file);
  });

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

/**
 * Compresse une image en préservant la qualité visuelle :
 * - Largeur max 1800 px (ratio conservé)
 * - WebP qualité 85 si supporté (~35% plus léger qu'un JPEG visuellement équivalent)
 * - PNG transparent conservé en PNG
 */
export async function compressImage(file, opts = {}) {
  const maxWidth = opts.maxWidth ?? TARGET_MAX_WIDTH;
  const quality = opts.quality ?? TARGET_QUALITY;

  if (file.size > MAX_IMAGE_RAW_MB * 1024 * 1024) {
    throw new Error(
      `Image trop lourde (${(file.size / 1024 / 1024).toFixed(1)} Mo). Maximum autorisé : ${MAX_IMAGE_RAW_MB} Mo.`
    );
  }
  if (!file.type.startsWith("image/")) {
    throw new Error("Le fichier n'est pas une image valide.");
  }
  if (file.type === "image/svg+xml" || file.type === "image/gif") {
    return await readAsDataURL(file);
  }

  const dataUrl = await readAsDataURL(file);
  const img = await loadImage(dataUrl);

  let { width, height } = img;
  if (width > maxWidth) {
    height = Math.round((height * maxWidth) / width);
    width = maxWidth;
  }

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, 0, 0, width, height);

  const isPng = file.type === "image/png";

  // PNG transparent -> on garde le PNG (qualité lossless préservée)
  if (isPng) {
    return canvas.toDataURL("image/png");
  }

  // Sinon, WebP qualité 85 si supporté (rendu premium, fichier léger)
  if (supportsWebpEncode()) {
    const webp = canvas.toDataURL("image/webp", quality);
    if (webp.startsWith("data:image/webp")) {
      return webp;
    }
  }

  // Fallback JPEG (un peu plus haut pour compenser l'encodage moins efficace)
  return canvas.toDataURL("image/jpeg", Math.min(0.92, quality + 0.03));
}

export async function readVideo(file) {
  if (!file.type.startsWith("video/")) {
    throw new Error("Le fichier n'est pas une vidéo valide.");
  }
  if (file.size > MAX_VIDEO_MB * 1024 * 1024) {
    throw new Error(
      `Vidéo trop lourde (${(file.size / 1024 / 1024).toFixed(1)} Mo). Maximum autorisé : ${MAX_VIDEO_MB} Mo. Compressez la vidéo avant de l'importer.`
    );
  }
  return await readAsDataURL(file);
}

export async function compressMany(files, opts = {}) {
  const results = [];
  const errors = [];
  for (const f of Array.from(files)) {
    try {
      results.push(await compressImage(f, opts));
    } catch (e) {
      errors.push({ name: f.name, message: e.message });
    }
  }
  return { results, errors };
}

export async function readVideosMany(files) {
  const results = [];
  const errors = [];
  for (const f of Array.from(files)) {
    try {
      results.push(await readVideo(f));
    } catch (e) {
      errors.push({ name: f.name, message: e.message });
    }
  }
  return { results, errors };
}

export const LIMITS = {
  MAX_IMAGE_RAW_MB,
  MAX_VIDEO_MB,
  TARGET_MAX_WIDTH,
  TARGET_QUALITY,
};
